import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { useApiCall } from "@framework/react-hooks";
import type { ISpeedDataMarkResponse } from "@framework/types";

import { useSpeed } from "../../../../../../context/speed-context";
import { normalizeTrendRangeForRequest } from "../../../multi-analysis/utils/trend-series";
import type { ISpeedScreenFormTimePanelProps } from "../types";
import { BODE_MEASURE_TYPE, formatBodeUnitLabel, mapBodeDataMarks } from "./model";

interface IBodeMarksRequest {
  trainId: number;
  pointIds: string;
  startTimestamp: string;
  endTimestamp: string;
  measureType: number;
  controlParameterId?: number;
}

export const BodeTimePanel: FC<ISpeedScreenFormTimePanelProps> = ({
  trainId,
  globalRange,
  selectedParameter,
  controlUnits,
  onLoadingChange,
}) => {
  const { formatMessage } = useIntl();
  const { selectedPoints, setDataMarks, setDataMarksError, setDataMarksLoading } = useSpeed();
  const { start, end } = globalRange;
  const range = useMemo(() => normalizeTrendRangeForRequest({ start, end }), [start, end]);
  const pointIds = useMemo(
    () => JSON.stringify([...new Set(selectedPoints.map(point => point.id))].sort((left, right) => left - right)),
    [selectedPoints],
  );
  const controlParameterId = selectedParameter?.dataId;
  const requestKey =
    selectedPoints.length && range ? JSON.stringify([trainId, pointIds, range, controlParameterId]) : null;
  const [result, setResult] = useState<{ key: string; rows: ISpeedDataMarkResponse[]; error: boolean } | null>(null);
  const { fn: loadMarks } = useApiCall(
    (api, args: IBodeMarksRequest) =>
      api.fetchJson({ url: "/point-v2/speed/data-marks", method: "GET", data: args }) as Promise<
        ISpeedDataMarkResponse[]
      >,
    { success: false },
  );
  const loadMarksRef = useRef(loadMarks);
  useEffect(() => {
    loadMarksRef.current = loadMarks;
  }, [loadMarks]);

  useEffect(() => {
    if (!range || !requestKey) return;
    let cancelled = false;
    void loadMarksRef
      .current(undefined, {
        trainId,
        pointIds,
        ...range,
        measureType: BODE_MEASURE_TYPE,
        ...(controlParameterId !== undefined ? { controlParameterId } : {}),
      })
      .then(rows => {
        if (!cancelled) setResult({ key: requestKey, rows: rows ?? [], error: false });
      })
      .catch(() => {
        if (!cancelled) setResult({ key: requestKey, rows: [], error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [controlParameterId, pointIds, range, requestKey, trainId]);

  const settled = requestKey !== null && result?.key === requestKey;
  const isLoading = requestKey !== null && !settled;
  const marks = useMemo(
    () =>
      settled && !result.error
        ? mapBodeDataMarks(
            result.rows,
            selectedPoints,
            direction => formatMessage({ id: `enums.pointDirection.${direction}` }),
            selectedParameter
              ? {
                  dataId: selectedParameter.dataId,
                  title: selectedParameter.title,
                  units: selectedParameter.unitLabel ? controlUnits : formatBodeUnitLabel(controlUnits),
                }
              : null,
          )
        : [],
    [controlUnits, formatMessage, result, selectedParameter, selectedPoints, settled],
  );

  useEffect(() => {
    setDataMarks(marks);
    setDataMarksError(settled && !!result?.error);
    setDataMarksLoading(isLoading);
  }, [isLoading, marks, result?.error, setDataMarks, setDataMarksError, setDataMarksLoading, settled]);

  useEffect(() => {
    onLoadingChange(isLoading);
    return () => onLoadingChange(false);
  }, [isLoading, onLoadingChange]);

  return null;
};
